import { useMemo } from 'react';
import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Package, Pencil, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormHeader } from '@/components/forms';
import { TabsWithPersistence, ContentTagsField } from '@/components/shared';
import { PricingTiersSection } from '@/components/pricing-tiers';
import type { TranslationLanguage } from '@/components/translations';
import {
  type SubscriptionPlan,
  getSubscriptionPlanName,
} from './types';
import { SubscriptionPlanEditTab } from './components/SubscriptionPlanEditTab';
import { SubscriptionPlanCreateTab } from './components/SubscriptionPlanCreateTab';

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

function getStatusBadge(isActive: boolean) {
  return isActive ? (
    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
      Активен
    </Badge>
  ) : (
    <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">
      Неактивен
    </Badge>
  );
}

export function SubscriptionPlansForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<SubscriptionPlan>({
    resource: 'subscription-plans',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: isEdit ? 'show' : 'list',
    meta: { endpoint: 'admin' },
  });

  const plan = query?.data?.data;
  const refetch = query?.refetch;

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const languages: TranslationLanguage[] = (languagesResult?.data ?? []).map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName,
    flag: l.flag ?? l.flagEmoji ?? '🏳️',
  }));

  const tabs = useMemo(() => {
    if (isEdit && !plan) { return []; }

    const items = [];

    if (isEdit && plan) {
      items.push({
        value: 'edit',
        label: 'Редактирование',
        icon: <Pencil className="h-4 w-4" />,
        content: (
          <SubscriptionPlanEditTab
            plan={plan}
            onUpdated={() => { refetch?.(); }}
          />
        ),
      });

      items.push({
        value: 'pricing',
        label: 'Тарифы',
        icon: <Tag className="h-4 w-4" />,
        content: (
          <div className="space-y-4 pt-6">
            <PricingTiersSection
              productKey={{ subscriptionPlanId: plan.id }}
              languages={languages}
            />
          </div>
        ),
      });
    } else {
      items.push({
        value: 'edit',
        label: 'Редактирование',
        icon: <Pencil className="h-4 w-4" />,
        content: (
          <SubscriptionPlanCreateTab
            loading={formLoading}
            onFinish={async (v) => { await onFinish(v); }}
          />
        ),
      });

      items.push({
        value: 'pricing',
        label: 'Тарифы',
        icon: <Tag className="h-4 w-4" />,
        disabled: true,
        content: <div />,
      });
    }

    return items;
  }, [plan, isEdit, refetch, formLoading, languages, onFinish]);

  if (isEdit && query?.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isEdit && !plan) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Абонемент не найден</p>
        <Button variant="link" onClick={() => navigate('/subscription-plans')}>
          Вернуться к списку
        </Button>
      </div>
    );
  }

  const title = isEdit && plan ? getSubscriptionPlanName(plan) : 'Создать абонемент';

  return (
    <div className="space-y-6">
      <FormHeader
        title={title}
        onBack={() => navigate('/subscription-plans')}
        icon={<Package className="h-5 w-5" />}
        titleAddon={isEdit && plan ? getStatusBadge(plan.isActive) : undefined}
        sticky
      />

      <TabsWithPersistence
        tabs={tabs}
        defaultTab="edit"
        persistKey={isEdit ? 'subscription-plans-edit' : 'subscription-plans-create'}
        tabsListClassName="grid w-full grid-cols-2 lg:inline-flex lg:w-auto"
      />

      {isEdit && plan?.id ? (
        <div className="rounded-lg border bg-card p-4">
          <ContentTagsField entityType="SUBSCRIPTION_PLAN" entityId={plan.id} />
        </div>
      ) : null}
    </div>
  );
}
